import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';

import CreateWizardLayout from '../../../../../components/CreateWizardLayout';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import BoxedCustomControl from '../../../../../../../components/global/BoxedCustomControl';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import { FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS } from '../../../../../../shutterbug/views/SignupAlbumCreate/helpers';
import VariantA from './components/VariantA';
import VariantB from './components/VariantB';
import useCrazyEggConversions from '../../../../../../../services/crazy-egg/hooks/useCrazyEggConversions';
import { CRAZY_EGG_CONVERSION_EVENTS } from '../../../../../../../services/crazy-egg/constants';

const propTypes = {
  values: PropTypes.object,
  stepProps: PropTypes.shape({
    eventAddonItems: PropTypes.func,
    onEventAddonItemToggle: PropTypes.func,
    currentSelectedEventAddonItemIds: PropTypes.arrayOf(PropTypes.string),
    waldofiedStepCompleted: PropTypes.bool,
  }),
  setStepProps: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onPrevStep: PropTypes.func.isRequired,
};

const FreemiumEventCreateWaldoficationStep = ({
  values,
  stepProps,
  setStepProps,
  handleChange,
  handleSubmit,
  onPrevStep,
}) => {
  const {
    shouldUseIOSBundles,
    eventAddonItems,
    iOSEventAddonItemsBundle,
    currentSelectedEventAddonItemIds,
    onEventAddonItemToggle,
    eventType,
    waldofiedStepCompleted,
    waldofiedStepRendered,
    waldofiedStepVariant = FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_A,
  } = stepProps;

  const crazyEggConversions = useCrazyEggConversions();

  useEffect(() => {
    if (waldofiedStepRendered) {
      return;
    }

    if (
      waldofiedStepVariant ===
      FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_A
    ) {
      crazyEggConversions.onTrackConversionEvent(
        CRAZY_EGG_CONVERSION_EVENTS.GOLD_VARIANT_A_SEEN,
      );
    } else if (
      waldofiedStepVariant ===
      FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_B
    ) {
      crazyEggConversions.onTrackConversionEvent(
        CRAZY_EGG_CONVERSION_EVENTS.GOLD_VARIANT_B_SEEN,
      );
    }

    // Avoid a second tracking trigger.
    setStepProps({ ...stepProps, waldofiedStepRendered: true });
  }, [waldofiedStepVariant]);

  const waldofiedEventAddonItem = eventAddonItems.find(eventAddonItem =>
    eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED),
  );
  const completeBundleEventAddonItem = shouldUseIOSBundles
    ? iOSEventAddonItemsBundle
    : eventAddonItems.find(
        item =>
          item.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED) &&
          item.types.includes(EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK) &&
          item.types.includes(EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO),
      );

  if (!waldofiedEventAddonItem) {
    return null;
  }

  const isWaldofiedSelected =
    currentSelectedEventAddonItemIds.includes(waldofiedEventAddonItem?.id) ||
    currentSelectedEventAddonItemIds.includes(completeBundleEventAddonItem?.id);

  return (
    <CreateWizardLayout.Wrapper>
      <CreateWizardLayout.Content>
        {waldofiedStepVariant ===
          FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_A && (
          <Fragment>
            <VariantA eventType={eventType} />
            <BoxedCustomControl
              id="upgrade-waldofied"
              type="checkbox"
              label={
                <p className="mb-0">
                  Yes, go <span className="text-gold">Gold</span>{' '}
                  <span className="text-primary text-extra-bold text-xs">
                    (+$
                    {formatPriceForDisplay(waldofiedEventAddonItem.price.value)}
                    )
                  </span>
                </p>
              }
              onChange={() => {
                if (
                  !currentSelectedEventAddonItemIds.includes(
                    waldofiedEventAddonItem.id,
                  ) &&
                  !currentSelectedEventAddonItemIds.includes(
                    completeBundleEventAddonItem.id,
                  )
                ) {
                  onEventAddonItemToggle(waldofiedEventAddonItem.id);
                }

                setStepProps(prev => ({
                  ...prev,
                  waldofiedStepCompleted: true,
                }));
              }}
              checked={waldofiedStepCompleted && isWaldofiedSelected}
              className="freemium-event-create-album-item"
              boxStyle="outlined"
            />
            <BoxedCustomControl
              id="no-upgrade-waldofied"
              type="checkbox"
              label={<>No thanks</>}
              onChange={() => {
                if (
                  currentSelectedEventAddonItemIds.includes(
                    waldofiedEventAddonItem.id,
                  )
                ) {
                  onEventAddonItemToggle(waldofiedEventAddonItem.id);
                }

                if (
                  currentSelectedEventAddonItemIds.includes(
                    completeBundleEventAddonItem.id,
                  )
                ) {
                  onEventAddonItemToggle(completeBundleEventAddonItem.id);
                  handleChange({
                    target: {
                      name: 'promo_code',
                      value: '',
                    },
                  });
                }

                setStepProps(prev => ({
                  ...prev,
                  waldofiedStepCompleted: true,
                }));
              }}
              checked={waldofiedStepCompleted && !isWaldofiedSelected}
              className="freemium-event-create-album-item"
              boxStyle="outlined"
            />
          </Fragment>
        )}
        {waldofiedStepVariant ===
          FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_B && (
          <VariantB
            eventType={eventType}
            values={values}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            onPrevStep={onPrevStep}
            stepProps={stepProps}
            setStepProps={setStepProps}
          />
        )}
      </CreateWizardLayout.Content>
    </CreateWizardLayout.Wrapper>
  );
};

FreemiumEventCreateWaldoficationStep.propTypes = propTypes;

export default FreemiumEventCreateWaldoficationStep;
